import "@/App.css";
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider, useTheme } from "@/context/ThemeContext";
import AppShell from "@/components/AppShell";
import { FullScreenLoader } from "@/components/FullScreenLoader";
import AuthCallback from "@/pages/AuthCallback";
import Login from "@/pages/Login";
import Onboarding from "@/pages/Onboarding";
import Discover from "@/pages/Discover";
import Watchlist from "@/pages/Watchlist";
import Seen from "@/pages/Seen";
import Settings from "@/pages/Settings";
import FaceOff from "@/pages/FaceOff";
import GenresEdit from "@/pages/GenresEdit";
import Rank from "@/pages/Rank";
import SearchPage from "@/pages/Search";
import { useEffect, useRef, useState } from "react";

// Visitors without an account get a guest session automatically, so every feature works without logging in.
function useGuestSession(user, startGuest, signedOut) {
  const started = useRef(false);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (user !== false || signedOut || started.current) return;
    started.current = true;
    startGuest().catch(() => setFailed(true)).finally(() => { started.current = false; });
  }, [user, startGuest, signedOut]);
  return failed;
}

function Protected() {
  const { user, startGuest, signedOut } = useAuth();
  const location = useLocation();
  const failed = useGuestSession(user, startGuest, signedOut);
  if (failed || (user === false && signedOut)) return <Navigate to="/login" replace />;
  if (!user) return <FullScreenLoader />;
  if (!user.onboarded && location.pathname !== "/onboarding") return <Navigate to="/onboarding" replace />;
  if (user.onboarded && location.pathname === "/onboarding") return <Navigate to="/" replace />;
  return <Outlet />;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <FullScreenLoader />;
  if (user && !user.is_guest) return <Navigate to={user.onboarded ? "/" : "/onboarding"} replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/try" element={<Navigate to="/" replace />} />
      <Route element={<Protected />}>
        <Route path="/onboarding" element={<Onboarding />} />
        <Route element={<AppShell />}>
          <Route path="/" element={<Discover />} />
          <Route path="/watchlist" element={<Watchlist />} />
          <Route path="/seen" element={<Seen />} />
          <Route path="/faceoff" element={<FaceOff />} />
          <Route path="/rank" element={<Rank />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/genres" element={<GenresEdit />} />
          <Route path="/settings" element={<Settings />} />
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function ThemedToaster() {
  const { theme } = useTheme();
  return <Toaster theme={theme} position="top-center" offset={76} mobileOffset={76} visibleToasts={2} richColors />;
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <div className="grain min-h-screen">
            <AppRouter />
          </div>
          <ThemedToaster />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}
